import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import formService from '../../services/formService';
import projectService from '../../services/projectService';
import FormBuilderModal from '../../components/common/FormBuilderModal';
import { useAuth } from '../../contexts/AuthContext';
import { CATEGORY_LABEL, formatCategory } from '../inspections/categoryOptions';
import '../monitoring/Monitoring.css';

const PUBLIC_APP_URL = process.env.REACT_APP_PUBLIC_FORM_URL || window.location.origin;

/**
 * Manage shareable, unauthenticated intake forms - anonymous GBV/SEA
 * reporting, stakeholder consultation feedback, road safety tips, etc.
 * Generic across every category rather than a separate feature per one.
 */
const PublicFormsList = () => {
  const navigate = useNavigate();
  const { user, organisation } = useAuth();
  const canDelete = user?.role === 'super_admin' || user?.accessLevel === 'admin';

  // Dashboard shortcuts (e.g. "GBV tracker", "Stakeholder register") deep-link
  // here with ?category=<x> so the list is pre-filtered and a new form
  // starts with the right category already selected.
  const [searchParams] = useSearchParams();
  const categoryFilter = searchParams.get('category') || '';

  const [projects, setProjects] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const visibleForms = useMemo(
    () => (categoryFilter ? forms.filter((f) => f.category === categoryFilter) : forms),
    [forms, categoryFilter]
  );

  const fetchProjects = useCallback(async () => {
    try {
      const data = await projectService.getAll();
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  }, []);

  const fetchForms = useCallback(async () => {
    try {
      setLoading(true);
      const data = await formService.getAll();
      setForms(data.forms || []);
    } catch (error) {
      console.error('Failed to fetch forms:', error);
      toast.error(error.response?.data?.error || 'Failed to load forms');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);
  useEffect(() => { fetchForms(); }, [fetchForms]);

  const handleCreate = async (formData) => {
    try {
      setSaving(true);
      await formService.create(formData);
      toast.success('Form created successfully');
      setShowCreateModal(false);
      fetchForms();
    } catch (error) {
      console.error('Failed to create form:', error);
      toast.error(error.response?.data?.error || 'Failed to create form');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (formData) => {
    try {
      setSaving(true);
      await formService.update(editingForm.id, formData);
      toast.success('Form updated successfully');
      setEditingForm(null);
      fetchForms();
    } catch (error) {
      console.error('Failed to update form:', error);
      toast.error(error.response?.data?.error || 'Failed to update form');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (form) => {
    try {
      await formService.update(form.id, { isActive: !form.isActive });
      toast.success(form.isActive ? 'Form closed' : 'Form reopened');
      fetchForms();
    } catch (error) {
      console.error('Failed to update form:', error);
      toast.error(error.response?.data?.error || 'Failed to update form');
    }
  };

  const handleDelete = async (form) => {
    if (!window.confirm(`Delete "${form.title}"? This also deletes all of its submissions.`)) return;
    try {
      await formService.delete(form.id);
      toast.success('Form deleted');
      fetchForms();
    } catch (error) {
      console.error('Failed to delete form:', error);
      toast.error(error.response?.data?.error || 'Failed to delete form');
    }
  };

  const copyLink = (form) => {
    const url = `${PUBLIC_APP_URL}/public/forms/${form.slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Public link copied to clipboard');
  };

  const stats = {
    total: visibleForms.length,
    active: visibleForms.filter((f) => f.isActive).length,
    submissions: visibleForms.reduce((sum, f) => sum + (f.submissionCount || 0), 0),
  };

  return (
    <div className="monitoring-page">
      <div className="monitoring-header">
        <div>
          <h1><i className="fas fa-file-signature"></i> Forms</h1>
          <p className="header-subtitle">
            {categoryFilter
              ? `Shareable links for anonymous ${CATEGORY_LABEL[categoryFilter] || formatCategory(categoryFilter)} reports and feedback.`
              : 'Shareable links for anonymous reports and feedback - GBV reporting, stakeholder consultation, road safety tips, and more.'}
          </p>
        </div>
        <button className="btn-primary-green" onClick={() => setShowCreateModal(true)}>
          <i className="fas fa-plus"></i> New Form
        </button>
      </div>

      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-icon"><i className="fas fa-file-signature"></i></div>
          <div><div className="stat-label">Total Forms</div><div className="stat-value">{stats.total}</div></div>
        </div>
        <div className="stat-card success">
          <div className="stat-icon"><i className="fas fa-check-circle"></i></div>
          <div><div className="stat-label">Active</div><div className="stat-value">{stats.active}</div></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-inbox"></i></div>
          <div><div className="stat-label">Total Submissions</div><div className="stat-value">{stats.submissions}</div></div>
        </div>
      </div>

      {loading ? (
        <div className="empty-state">
          <i className="fas fa-spinner fa-spin"></i>
          <h3>Loading forms...</h3>
        </div>
      ) : visibleForms.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-file-signature"></i>
          <h3>No forms yet</h3>
          <p>
            {categoryFilter
              ? `Create a ${(CATEGORY_LABEL[categoryFilter] || formatCategory(categoryFilter)).toLowerCase()} form to start collecting anonymous reports or feedback via a shareable link.`
              : 'Create a form to start collecting anonymous reports or feedback via a shareable link.'}
          </p>
          <button className="btn-text" onClick={() => setShowCreateModal(true)}>
            <i className="fas fa-plus"></i> New Form
          </button>
        </div>
      ) : (
        <div className="monitoring-list">
          {visibleForms.map((form) => (
            <div key={form.id} className="monitoring-card">
              <div className="monitoring-card-main">
                <h4>{form.title}</h4>
                <div className="monitoring-card-meta">
                  <span className="category-badge">{CATEGORY_LABEL[form.category] || formatCategory(form.category)}</span>
                  <span><i className="fas fa-project-diagram"></i> {form.project?.name || 'Organisation-wide'}</span>
                  <span><i className="fas fa-inbox"></i> {form.submissionCount || 0} submission{form.submissionCount === 1 ? '' : 's'}</span>
                </div>
              </div>
              <div className="monitoring-card-actions">
                <span className={`status-badge status-${form.isActive ? 'active' : 'expired'}`}>
                  {form.isActive ? 'Active' : 'Closed'}
                </span>
                <button className="btn-icon" title="Copy public link" onClick={() => copyLink(form)}>
                  <i className="fas fa-link"></i>
                </button>
                <button className="btn-icon" title="View submissions" onClick={() => navigate(`/forms/${form.id}/submissions`)}>
                  <i className="fas fa-inbox"></i>
                </button>
                <button className="btn-icon" title={form.isActive ? 'Close form' : 'Reopen form'} onClick={() => handleToggleActive(form)}>
                  <i className={`fas ${form.isActive ? 'fa-pause' : 'fa-play'}`}></i>
                </button>
                <button className="btn-icon" title="Edit" onClick={() => setEditingForm(form)}>
                  <i className="fas fa-edit"></i>
                </button>
                {canDelete && (
                  <button className="btn-icon danger" title="Delete" onClick={() => handleDelete(form)}>
                    <i className="fas fa-trash"></i>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreateModal && (
        <FormBuilderModal
          isOpen
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreate}
          saving={saving}
          projects={projects}
          initialCategory={categoryFilter || null}
          orgTrackingCategories={organisation?.trackingCategories}
          orgCustomCategories={organisation?.customCategories || []}
        />
      )}
      {editingForm && (
        <FormBuilderModal
          isOpen
          onClose={() => setEditingForm(null)}
          onSave={handleUpdate}
          saving={saving}
          projects={projects}
          form={editingForm}
          orgTrackingCategories={organisation?.trackingCategories}
          orgCustomCategories={organisation?.customCategories || []}
        />
      )}
    </div>
  );
};

export default PublicFormsList;
